// ─── client-placed driver: local-pipeline ────────────────────────────────────
// The reason placement exists. A Cloudflare Worker cannot reach the boss's
// `localhost`, so for a local model the browser runs the provider itself:
// STT → LLM (+tools) → TTS against `clientConfig`'s endpoints (LM Studio /
// MiniCPM-o / whisper, default http://localhost:1234/v1). The audio never leaves
// the machine; the session is kept authoritative by reporting transcripts and tool
// calls up the socket.
//
// The loop is `shared/local-pipeline.ts` — the same code the DO runs for
// `placement: 'server'`, so the two cannot drift. This file is the browser half of
// its `PipelineHost`, plus the narrowing of the `clientConfig` it arrived with.

import { LocalPipeline, type PipelineSettings } from '@shared/local-pipeline.ts'
import type { ChatMessage, Endpoint } from '@shared/openai-http.ts'
import type { ClientProviderConfig, Role } from '@shared/protocol.ts'
import { DEFAULT_AUDIO } from '@shared/protocol.ts'
import { DriverConfigError, type ClientDriver, type DriverHost, type DriverOptions } from './types.ts'

/** Conversation the local model is given. The session keeps the real transcript. */
const MAX_CONTEXT_TURNS = 20

/**
 * Narrows the provider's `clientConfig` (typed as an open bag on the wire) into what
 * the pipeline needs. Fails loudly and specifically: a client-placed session with a
 * malformed config would otherwise present as a dead mic.
 */
export function readPipelineConfig(raw: ClientProviderConfig): PipelineSettings {
  const missing: string[] = []
  const endpoint = (key: 'stt' | 'llm' | 'tts'): Endpoint => {
    const v = raw[key] as Partial<Endpoint> | undefined
    if (!v || typeof v.baseUrl !== 'string' || typeof v.model !== 'string') missing.push(`${key}: { baseUrl, model }`)
    return { baseUrl: v?.baseUrl ?? '', model: v?.model ?? '', apiKey: typeof v?.apiKey === 'string' ? v.apiKey : undefined }
  }
  const stt = endpoint('stt')
  const llm = endpoint('llm')
  const tts = endpoint('tts')
  if (typeof raw.persona !== 'string' || !raw.persona) missing.push('persona')
  if (missing.length) {
    throw new DriverConfigError(`clientConfig for "${raw.provider}" is missing ${missing.join(', ')}`)
  }
  const audio = raw.audio as PipelineSettings['audio'] | undefined
  return {
    stt,
    llm,
    tts,
    persona: raw.persona as string,
    voice: typeof raw.voice === 'string' ? raw.voice : undefined,
    locale: typeof raw.locale === 'string' ? raw.locale : undefined,
    audio: { input: audio?.input ?? DEFAULT_AUDIO, output: audio?.output ?? DEFAULT_AUDIO },
  }
}

export function createLocalPipeline(raw: ClientProviderConfig, host: DriverHost, opts: DriverOptions = {}): ClientDriver {
  // In client placement the session's `clientRelay` cannot speak, so the greeting
  // is ours to say.
  const settings: PipelineSettings = { ...readPipelineConfig(raw), greeting: opts.greeting }

  // The session owns the transcript; the model only needs a recent tail of it.
  const context: ChatMessage[] = []
  const remember = (role: Role, text: string) => {
    context.push({ role, content: text })
    if (context.length > MAX_CONTEXT_TURNS) context.splice(0, context.length - MAX_CONTEXT_TURNS)
  }

  const pipeline = new LocalPipeline(settings, {
    fetch: host.fetch,
    context: () => context,
    record: (role, text) => {
      remember(role, text)
      // The session owns the transcript; it only knows what we tell it.
      host.emit({ t: 'transcript', role, text, final: true })
    },
    remember,
    /**
     * Assistant speech boundaries stay local: `ClientEvent`'s speech events mean
     * *user* speech (`speech.start` is the barge-in signal), so there is no way —
     * and no need — to report our own turn boundaries up. The face is driven from
     * the audio itself.
     */
    speaking: () => {},
    play: (audio) => host.play(audio),
    tools: () => host.tools(),
    runTool: (call) => host.runTool(call.name, call.args),
    fail: (message) => host.fail(`local-pipeline: ${message}`),
    // Nothing to keep alive: the browser tab outlives the turn.
    background: () => {},
  })

  return {
    id: 'local-pipeline',
    start: () => pipeline.start(),
    pushAudio: (pcm) => pipeline.pushAudio(pcm),
    commit: () => pipeline.commit(),
    say: (text, role) => void pipeline.say(text, role),
    cancel: () => pipeline.cancel(),
    close: () => pipeline.close(),
  }
}
